/** @jsxImportSource @emotion/react */
import { Box, Theme, Typography, useTheme } from '@mui/material';
import { useTemplatesList } from 'api/template';
import TabGroupIcon from 'components/icons/TabGroupIcon';
import { useCategoryRepresentations } from 'hooks/useCategoryRepresentation';
import { usePaths } from 'hooks/usePaths';
import React from 'react';
import { Link } from 'react-router-dom';
import { StylesClassName, UseStyles } from 'types/styles';
import setSpacing from 'utils/spacing';

interface IProposalsProps {
  className?: StylesClassName;
}

const Proposals: React.FC<IProposalsProps> = ({ className, ...rest }) => {
  const { data: templates, isLoading } = useTemplatesList();
  const categories = useCategoryRepresentations(templates ?? []);
  const theme = useTheme() as Theme;
  const classes = useStyles(theme);
  const paths = usePaths();

  if (isLoading || !templates?.length) {
    return null;
  }

  return (
    <Box css={[classes.Proposals, className]} {...rest}>
      <Typography mb={2} variant="subtitle1" css={classes.title}>
        Optional accelerated national planning process to support National Planning and Pandemic Fund proposals
      </Typography>
      <Box pl={2} mb={4} display="flex" alignItems="center" css={classes.arrowLine}>
        <Typography variant="body2" component="span">
          Integrated National Investment Plan and Fund application are generated following completion and upload of
          templates (voluntary)
        </Typography>
      </Box>
      <Box css={classes.main} display="flex" alignItems="center">
        <Box css={classes.items}>
          {Object.entries(categories).map(([id, category], index) =>
            !category.isFakeCategory ? (
              <Box key={id} style={{ backgroundColor: category.bg }} css={classes.item}>
                <span css={classes.num} style={{ color: category.numColor }}>
                  {index + 1}
                </span>
                <Link to={paths.templates.generate() + '#' + id} style={{ color: category.color }} css={classes.name}>
                  {category.name}
                </Link>
                <span css={classes.arrow} className="item-arrow" style={{ color: category.bg }}></span>
              </Box>
            ) : null,
          )}
        </Box>
        <Box css={classes.box} ml={6}>
          <Box css={classes.boxTop} p={1} pl={2} pr={2}>
            <TabGroupIcon />
            <Link to={paths.templates.generate() + '#inpp'}>{categories?.['inpp']?.name}</Link>
          </Box>
          <Box css={classes.boxLeft} p={1}>
            {categories?.['inpp']?.templates?.[0]?.name}
          </Box>
          <Box css={classes.boxRight} p={1}>
            {categories?.['inpp']?.templates?.[1]?.name}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const useStyles = (theme: Theme): UseStyles => ({
  Proposals: {},
  items: {
    display: 'flex',
    alignItems: 'stretch',
    width: '100%',
  },
  title: {
    fontWeight: 600,
    color: theme.palette.custom.primary.brand[150],
  },
  arrowLine: {
    color: theme.palette.custom.secondary.purple[120],
    background: theme.palette.custom.secondary.purple[20],
    borderRadius: '8px 0 0 8px',
    marginRight: 29,
    position: 'relative',
    height: 32,
    verticalAlign: 'middle',

    '&::after': {
      content: "''",
      display: 'block',
      width: 0,
      height: 0,
      borderStyle: 'solid',
      borderColor: `transparent ${theme.palette.custom.secondary.purple[20]}`,
      borderWidth: '16px 0 16px 29px',
      position: 'absolute',
      right: -29,
      top: 0,
    },

    span: {
      fontWeight: 600,
      lineHeight: 1,
    },
  },
  item: {
    height: 120,
    width: 125,
    padding: setSpacing([2, 0, 2, 9]),
    flexShrink: 0,
    position: 'relative',

    '&:first-of-type': {
      width: 96,
      paddingLeft: theme.spacing(3),
      borderRadius: '8px 0 0 8px',
    },
    '&:last-of-type': {
      flexGrow: 1,

      '& .item-arrow': {
        '&::after': {
          borderWidth: '60px 0 60px 37px',
        },
        '&::before': {
          display: 'none',
        },
      },
    },
  },
  num: {
    width: 30,
    height: 30,
    borderRadius: '50%',
    background: theme.palette.custom.primary.grey[50],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: theme.typography.custom.text.xl.fontSize,
    marginBottom: theme.spacing(2),
  },
  name: {
    display: 'inline-block',
    fontSize: theme.typography.custom.text.xs.fontSize,
    lineHeight: 1.33,
    textDecoration: 'none',
  },
  arrow: {
    display: 'block',
    width: 3,
    height: '100%',
    position: 'absolute',
    top: 0,
    right: -3,
    background: theme.palette.common.white,
    zIndex: 1,

    '&::before, &::after': {
      content: '""',
      display: 'block',
      width: 0,
      height: 0,
      borderStyle: 'solid',
      borderColor: 'transparent currentColor',
      borderWidth: '60px 0 60px 29px',
      position: 'absolute',
      left: 0,
      top: 0,
    },
    '&::before': {
      borderColor: `transparent ${theme.palette.common.white}`,
      left: 3,
    },
  },
  box: {
    flexShrink: 0,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    background: theme.palette.custom.secondary.purple[120],
    borderRadius: 8,
    width: 170,
    height: 120,
    color: theme.palette.common.white,
    fontSize: theme.typography.custom.text.xs.fontSize,
    fontWeight: 600,
    lineHeight: 1.33,
    overflow: 'hidden',
    alignItems: 'stretch',
  },
  boxTop: {
    gridColumn: '1 / -1',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),

    a: {
      color: theme.palette.common.white,
      textDecoration: 'none',
    },
  },
  boxLeft: {
    textAlign: 'center',
    background: theme.palette.custom.secondary.purple[110],
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  boxRight: {
    textAlign: 'center',
    background: theme.palette.custom.secondary.purple[90],
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
});

export default Proposals;
